import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Printer, Filter, Download, ClipboardList, BedDouble, Ambulance, IndianRupee, TrendingUp } from "lucide-react";
import PaymentModeEditor from "@/components/PaymentModeEditor";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const firstOfMonth = () => new Date().toISOString().slice(0, 7) + "-01";
const firstOfYear = () => new Date().getFullYear() + "-01-01";

const PRESETS = [
  { label: "Today", from: today(), to: today() },
  { label: "Yesterday", from: daysAgo(1), to: daysAgo(1) },
  { label: "This Week", from: daysAgo(6), to: today() },
  { label: "Last 15 Days", from: daysAgo(14), to: today() },
  { label: "This Month", from: firstOfMonth(), to: today() },
  { label: "This Year", from: firstOfYear(), to: today() },
];

const csvEscape = (v) => {
  if (v == null) return "";
  const s = String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) return '"' + s.replace(/"/g, '""') + '"';
  return s;
};

const downloadCSV = (name, rows) => {
  if (!rows || !rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => csvEscape(r[k])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name}.csv`;
  a.click();
};

const KPI = ({ label, val, sub, tone = "blue", icon: Ic }) => (
  <div className="bg-white border p-3">
    <div className="flex justify-between">
      <div>
        <div className="label-tiny">{label}</div>
        <div className="font-display text-xl font-semibold mt-1 data-mono">{val}</div>
        {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
      </div>
      {Ic && <div className={`w-8 h-8 flex items-center justify-center bg-${tone}-50 text-${tone}-700`}><Ic className="w-4 h-4"/></div>}
    </div>
  </div>
);

export default function Reports() {
  const { user } = useAuth();
  const canVoidPayment = user?.role === "super_admin";
  const canRestorePayment = user?.role === "super_admin";
  const canEditPayment = ["admin", "super_admin", "billing", "accounts"].includes(user?.role);
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [doctorId, setDoctorId] = useState("");
  const [department, setDepartment] = useState("");
  const [category, setCategory] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/doctors").then((r) => setDoctors(r.data)); }, []);

  const load = useCallback(() => {
    const params = { from_date: from, to_date: to };
    if (doctorId) params.doctor_id = doctorId;
    if (department) params.department = department;
    if (category) params.category = category;
    api.get("/reports/full", { params }).then((r) => setData(r.data));
  }, [from, to, doctorId, department, category]);
  useEffect(() => { load(); }, [load]);

  const voidPayment = async (pid) => {
    const reason = window.prompt("Reason for voiding this payment (audit-logged):");
    if (!reason || !reason.trim()) return;
    try {
      await api.delete(`/payments/${pid}`, { params: { reason: reason.trim() } });
      toast.success("Payment voided");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
  };
  const restorePayment = async (pid) => {
    if (!window.confirm("Restore this voided payment?")) return;
    try { await api.post(`/payments/${pid}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };
  const voidBillItem = async (bid) => {
    const reason = window.prompt("Reason for voiding this bill item (audit-logged):");
    if (!reason || !reason.trim()) return;
    try {
      await api.delete(`/bill-items/${bid}`, { params: { reason: reason.trim() } });
      toast.success("Bill item voided");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
  };
  const restoreBillItem = async (bid) => {
    if (!window.confirm("Restore this voided bill item?")) return;
    try { await api.post(`/bill-items/${bid}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };
  const [editingPay, setEditingPay] = useState(null);
  const editPayment = (pay) => setEditingPay(pay);

  // Daily expense entry — adds to today's report (and to the printed Divine replica)
  const [expForm, setExpForm] = useState({ description: "", amount: "", category: "Operations", paid_mode: "Cash" });
  const [expSaving, setExpSaving] = useState(false);
  const [expCategories, setExpCategories] = useState([]);
  const [newCatName, setNewCatName] = useState("");
  const [catMgrOpen, setCatMgrOpen] = useState(false);

  const loadCategories = useCallback(() => {
    api.get("/expense-categories").then((r) => {
      setExpCategories(r.data || []);
      if (r.data?.length && !r.data.find((c) => c.name === expForm.category)) {
        setExpForm((p) => ({ ...p, category: r.data[0].name }));
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const addCategory = async () => {
    const nm = newCatName.trim();
    if (!nm) return toast.error("Enter a category name");
    try {
      const { data } = await api.post("/expense-categories", { name: nm });
      toast.success(`Added "${data.name}"`);
      setNewCatName("");
      setExpForm((p) => ({ ...p, category: data.name }));
      loadCategories();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const renameCategory = async (cid, current) => {
    const next = window.prompt("Rename category:", current);
    if (!next || !next.trim() || next === current) return;
    try { await api.put(`/expense-categories/${cid}`, { name: next.trim() }); toast.success("Renamed"); loadCategories(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Rename failed"); }
  };
  const deleteCategory = async (cid, name) => {
    if (!window.confirm(`Delete category "${name}"? Only possible if no expense currently uses it.`)) return;
    try { await api.delete(`/expense-categories/${cid}`); toast.success("Deleted"); loadCategories(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const addExpense = async () => {
    if (!expForm.description.trim() || !Number(expForm.amount)) return toast.error("Description & amount required");
    setExpSaving(true);
    try {
      await api.post("/expenses", { ...expForm, amount: Number(expForm.amount), expense_date: from });
      toast.success("Expense recorded");
      setExpForm({ description: "", amount: "", category: "Operations", paid_mode: "Cash" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setExpSaving(false); }
  };
  const deleteExpense = async (eid) => {
    const reason = window.prompt("Reason for deleting this expense:");
    if (!reason || !reason.trim()) return;
    try { await api.delete(`/expenses/${eid}`, { params: { reason: reason.trim() } }); toast.success("Expense deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const depts = useMemo(() => [...new Set(doctors.map((d) => d.department))], [doctors]);
  const rangeLabel = from === to ? new Date(from).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : `${new Date(from).toLocaleDateString("en-IN")} → ${new Date(to).toLocaleDateString("en-IN")}`;

  const openPrint = () => {
    const params = new URLSearchParams({ from_date: from, to_date: to });
    if (doctorId) params.set("doctor_id", doctorId);
    if (department) params.set("department", department);
    if (category) params.set("category", category);
    window.open(`/reports/print?${params.toString()}`, "_blank");
  };

  if (!data) return <div>Loading reports...</div>;
  const t = data.totals;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Reports & Records</h1>
          <p className="text-sm text-slate-500">Collection, OPD, IPD, Cash / Credit / Debit, doctor and department breakdowns for {rangeLabel}.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openPrint} className="rounded-sm" data-testid="rep-print"><Printer className="w-4 h-4 mr-1"/> Print Report</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500"/>
          <Label className="label-tiny">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="rep-from"/>
          <Label className="label-tiny">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="rep-to"/>
          <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)} className="h-9 border text-sm px-2" data-testid="rep-doctor"><option value="">All Doctors</option>{doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
          <select value={department} onChange={(e) => setDepartment(e.target.value)} className="h-9 border text-sm px-2"><option value="">All Departments</option>{depts.map((d) => <option key={d}>{d}</option>)}</select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 border text-sm px-2"><option value="">All Categories</option>{["Consultation","Room","Doctor Visit","RMO","BMW","Medicine","Lab","OT","Surgery","Emergency","Implant","Anesthesia","Blood","Other"].map((c) => <option key={c}>{c}</option>)}</select>
        </div>
        <div className="flex flex-wrap gap-1">
          {PRESETS.map((p) => (
            <button key={p.label} data-testid={`rep-preset-${p.label.replace(/\s/g,'-').toLowerCase()}`} onClick={() => { setFrom(p.from); setTo(p.to); }} className={`text-xs px-2.5 py-1 border rounded-sm ${from === p.from && to === p.to ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 hover:border-blue-500"}`}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* Daily Expenses — recorded against the "from" date so they show up on that day's printed report */}
      <div className="bg-white border p-3" data-testid="daily-expenses-card">
        <div className="flex items-center justify-between mb-2">
          <span className="label-tiny">Daily Expenses · entered for {from}</span>
          <div className="flex items-center gap-3 text-xs">
            <button onClick={() => setCatMgrOpen((v) => !v)} className="text-blue-700 hover:text-blue-900 underline underline-offset-2" data-testid="exp-cat-mgr-toggle">
              {catMgrOpen ? "Hide" : "Manage"} categories ({expCategories.length})
            </button>
            <span className="text-slate-500">
              Total: <b className="font-mono">{fmtINR((data?.expenses || []).reduce((s, e) => s + Number(e.amount || 0), 0))}</b>
            </span>
          </div>
        </div>

        {catMgrOpen && (
          <div className="border border-slate-300 bg-slate-50 rounded-sm p-2 mb-3" data-testid="exp-cat-manager">
            <div className="label-tiny mb-1">Expense Category Master</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {expCategories.map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1 bg-white border border-slate-300 rounded-sm px-1.5 py-0.5 text-xs" data-testid={`exp-cat-${c.id}`}>
                  {c.name}
                  <button onClick={() => renameCategory(c.id, c.name)} className="text-blue-600 hover:text-blue-800 text-[10px]" title="Rename" data-testid={`exp-cat-rename-${c.id}`}>✎</button>
                  <button onClick={() => deleteCategory(c.id, c.name)} className="text-red-600 hover:text-red-800 text-[10px]" title="Delete" data-testid={`exp-cat-del-${c.id}`}>×</button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input placeholder="New category name" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategory()} className="h-8 text-sm flex-1 max-w-xs" data-testid="exp-cat-new-input"/>
              <Button onClick={addCategory} className="h-8 bg-blue-700 hover:bg-blue-800 rounded-sm text-xs" data-testid="exp-cat-add">+ Add</Button>
              <span className="text-[10px] text-slate-500">Delete is blocked if any expense still uses the category — rename instead.</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-6 gap-2 mb-2">
          <Input placeholder="Description (e.g. Diesel for genset)" value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} className="col-span-2 h-9" data-testid="exp-desc"/>
          <Input type="number" placeholder="Amount" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} className="h-9" data-testid="exp-amount"/>
          <select value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} className="h-9 border text-sm px-2" data-testid="exp-category">
            {expCategories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <select value={expForm.paid_mode} onChange={(e) => setExpForm({ ...expForm, paid_mode: e.target.value })} className="h-9 border text-sm px-2" data-testid="exp-mode">
            {["Cash","GPay","UPI","Card","Bank"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <Button onClick={addExpense} disabled={expSaving} data-testid="exp-add" className="bg-blue-700 hover:bg-blue-800 rounded-sm h-9">+ Record</Button>
        </div>
        {(data?.expenses || []).length > 0 && (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500 border-b"><th className="py-1">Date</th><th>Description</th><th>Category</th><th>Mode</th><th>By</th><th className="text-right">Amount</th><th></th></tr></thead>
            <tbody>
              {(data?.expenses || []).map((e) => (
                <tr key={e.id} className="border-b" data-testid={`exp-row-${e.id}`}>
                  <td className="py-1 font-mono text-[11px]">{e.expense_date}</td>
                  <td>{e.description}{e.remarks ? ` · ${e.remarks}` : ""}</td>
                  <td>{e.category}</td>
                  <td>{e.paid_mode}</td>
                  <td className="text-[11px] text-slate-500">{e.created_by_name}</td>
                  <td className="text-right font-mono">{fmtINR(e.amount)}</td>
                  <td className="text-right">
                    <button onClick={() => deleteExpense(e.id)} className="text-[10px] text-red-700 border border-red-300 rounded-sm px-1.5 py-0.5 hover:bg-red-50" data-testid={`exp-del-${e.id}`}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPI label="Total OPD" val={t.opd_count} icon={ClipboardList}/>
        <KPI label="Total IPD" val={t.ipd_count} icon={BedDouble}/>
        <KPI label="Emergency" val={t.emergency_count} icon={Ambulance} tone="red"/>
        <KPI label="Total Collection" val={fmtINR(t.collection)} icon={IndianRupee} tone="teal"/>
        <KPI label="Advance Received" val={fmtINR(t.advance)} tone="blue"/>
        <KPI label="Refunds" val={fmtINR(t.refund)} tone="red"/>
      </div>

      {/* Payment Mode breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Cash" val={fmtINR(t.cash)} tone="amber"/>
        <KPI label="GPay" val={fmtINR(t.gpay)} tone="teal"/>
        <KPI label="UPI" val={fmtINR(t.upi)} tone="blue"/>
        <KPI label="Card" val={fmtINR(t.card)} tone="blue"/>
      </div>

      <Tabs defaultValue="summary">
        <TabsList className="bg-white border flex-wrap h-auto">
          <TabsTrigger value="summary" data-testid="rep-tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="day" data-testid="rep-tab-day">Day-wise</TabsTrigger>
          <TabsTrigger value="doctor" data-testid="rep-tab-doctor">Doctor-wise</TabsTrigger>
          <TabsTrigger value="department" data-testid="rep-tab-department">Department</TabsTrigger>
          <TabsTrigger value="category" data-testid="rep-tab-category">Category</TabsTrigger>
          <TabsTrigger value="opd" data-testid="rep-tab-opd">OPD List</TabsTrigger>
          <TabsTrigger value="ipd" data-testid="rep-tab-ipd">IPD List</TabsTrigger>
          <TabsTrigger value="payments" data-testid="rep-tab-payments">Cash / Credits</TabsTrigger>
          <TabsTrigger value="advances" data-testid="rep-tab-advances">Advances</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="rep-tab-refunds">Refunds</TabsTrigger>
          <TabsTrigger value="items" data-testid="rep-tab-items">Debit (Charges)</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border">
              <div className="p-3 border-b label-tiny">Payment Modes</div>
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Mode</th><th className="label-tiny text-center">Txn Count</th><th className="label-tiny text-right">Amount</th></tr></thead>
                <tbody>
                  {data.modes.map((m) => (
                    <tr key={m.mode} className="border-b"><td className="p-2">{m.mode}</td><td className="text-center font-mono">{m.count}</td><td className="text-right font-mono">{fmtINR(m.amount)}</td></tr>
                  ))}
                  {data.modes.length === 0 && <tr><td colSpan="3" className="p-4 text-center text-sm text-slate-500">No payments in range</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="bg-white border">
              <div className="p-3 border-b label-tiny">Top Categories</div>
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Category</th><th className="label-tiny text-center">Count</th><th className="label-tiny text-right">Total</th></tr></thead>
                <tbody>
                  {data.categories.slice(0, 12).map((c) => (
                    <tr key={c.category} className="border-b"><td className="p-2">{c.category}</td><td className="text-center font-mono">{c.count}</td><td className="text-right font-mono">{fmtINR(c.amount)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="day">
          <div className="bg-white border">
            <div className="p-3 border-b flex justify-between items-center"><span className="label-tiny">Day-wise Breakdown</span><Button size="sm" variant="outline" onClick={() => downloadCSV(`day_report_${from}_to_${to}`, data.day_breakdown)}><Download className="w-3 h-3 mr-1"/> CSV</Button></div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Date</th><th className="label-tiny text-center">OPD</th><th className="label-tiny text-center">IPD</th><th className="label-tiny text-right">Collection</th><th className="label-tiny text-right">Advance</th><th className="label-tiny text-right">Refund</th></tr></thead>
              <tbody>
                {data.day_breakdown.map((d) => (
                  <tr key={d.date} className="border-b"><td className="p-2 font-mono">{d.date}</td><td className="text-center font-mono">{d.opd}</td><td className="text-center font-mono">{d.ipd}</td><td className="text-right font-mono">{fmtINR(d.collection)}</td><td className="text-right font-mono text-teal-700">{fmtINR(d.advance)}</td><td className="text-right font-mono text-red-700">{fmtINR(d.refund)}</td></tr>
                ))}
                <tr className="bg-slate-100 font-semibold"><td className="p-2">TOTAL</td><td className="text-center font-mono">{t.opd_count}</td><td className="text-center font-mono">{t.ipd_count}</td><td className="text-right font-mono">{fmtINR(t.collection)}</td><td className="text-right font-mono text-teal-700">{fmtINR(t.advance)}</td><td className="text-right font-mono text-red-700">{fmtINR(t.refund)}</td></tr>
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="doctor">
          <div className="bg-white border">
            <div className="p-3 border-b flex justify-between items-center"><span className="label-tiny">Doctor-wise Revenue Breakdown</span><Button size="sm" variant="outline" onClick={() => downloadCSV(`doctor_report_${from}_to_${to}`, data.doctor_breakdown)}><Download className="w-3 h-3 mr-1"/> CSV</Button></div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Doctor</th><th className="label-tiny">Department</th><th className="label-tiny text-center">Items</th><th className="label-tiny text-right">OPD</th><th className="label-tiny text-right">IPD/Visit</th><th className="label-tiny text-right">OT/Surgery</th><th className="label-tiny text-right">Lab</th><th className="label-tiny text-right">Other</th><th className="label-tiny text-right">Total</th></tr></thead>
              <tbody>
                {data.doctor_breakdown.map((d) => (
                  <tr key={d.doctor} className="border-b">
                    <td className="p-2 font-medium">{d.doctor}</td>
                    <td className="text-xs">{d.department}</td>
                    <td className="text-center font-mono">{d.count}</td>
                    <td className="text-right font-mono">{fmtINR(d.opd_amount)}</td>
                    <td className="text-right font-mono">{fmtINR(d.ipd_amount)}</td>
                    <td className="text-right font-mono">{fmtINR(d.ot_amount)}</td>
                    <td className="text-right font-mono">{fmtINR(d.lab_amount)}</td>
                    <td className="text-right font-mono">{fmtINR(d.other_amount)}</td>
                    <td className="text-right font-mono font-semibold text-blue-700">{fmtINR(d.total)}</td>
                  </tr>
                ))}
                {data.doctor_breakdown.length === 0 && <tr><td colSpan="9" className="p-4 text-center text-sm text-slate-500">No doctor billing in range</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="department">
          <div className="bg-white border">
            <div className="p-3 border-b label-tiny">Department-wise Revenue</div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Department</th><th className="label-tiny text-center">Items</th><th className="label-tiny text-right">Amount</th></tr></thead>
              <tbody>
                {data.department_breakdown.map((d) => (
                  <tr key={d.department} className="border-b"><td className="p-2 font-medium">{d.department}</td><td className="text-center font-mono">{d.count}</td><td className="text-right font-mono">{fmtINR(d.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="category">
          <div className="bg-white border">
            <div className="p-3 border-b label-tiny">Category-wise Revenue (OPD / IPD / Plaster / Lab / OT / Medicine / Room / etc.)</div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Category</th><th className="label-tiny text-center">Items</th><th className="label-tiny text-right">Amount</th></tr></thead>
              <tbody>
                {data.categories.map((c) => (
                  <tr key={c.category} className="border-b"><td className="p-2">{c.category}</td><td className="text-center font-mono">{c.count}</td><td className="text-right font-mono">{fmtINR(c.amount)}</td></tr>
                ))}
                <tr className="bg-slate-100 font-semibold"><td className="p-2">TOTAL</td><td className="text-center font-mono">{data.categories.reduce((s, c) => s + c.count, 0)}</td><td className="text-right font-mono">{fmtINR(data.categories.reduce((s, c) => s + c.amount, 0))}</td></tr>
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="opd">
          <div className="bg-white border">
            <div className="p-3 border-b flex justify-between items-center"><span className="label-tiny">OPD List ({data.opds.length})</span><Button size="sm" variant="outline" onClick={() => downloadCSV(`opd_${from}_to_${to}`, data.opds.map((o) => ({ opd_no: o.opd_no, uhid: o.uhid, patient: o.patient_name, doctor: o.doctor_name, department: o.department, diagnosis: o.diagnosis, consultation_fee: o.consultation_fee, investigations_total: o.investigations_total || 0, cash: o.mode_split?.CASH || 0, upi: o.mode_split?.UPI || 0, pmjay: o.mode_split?.PMJAY || 0, package: o.mode_split?.PACKAGE || 0, card: o.mode_split?.CARD || 0, total: o.total_amount || (Number(o.consultation_fee||0)+Number(o.investigations_total||0)), date: o.created_at, status: o.status })))}><Download className="w-3 h-3 mr-1"/> CSV</Button></div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">OPD No</th><th className="label-tiny">UHID</th><th className="label-tiny">Patient</th><th className="label-tiny">Doctor / Dept</th><th className="label-tiny">Date</th><th className="label-tiny text-right">Consult</th><th className="label-tiny text-right">Investig.</th><th className="label-tiny text-right text-teal-700">Cash</th><th className="label-tiny text-right text-blue-700">UPI</th><th className="label-tiny text-right text-purple-700">PMJAY</th><th className="label-tiny text-right text-purple-700">Package</th><th className="label-tiny text-right">Card</th><th className="label-tiny text-right">Total</th></tr></thead>
              <tbody>
                {data.opds.map((o) => {
                  const isWalkin = o.is_walkin || (o.department === "Walk-in Investigation");
                  const consult = Number(o.consultation_fee || 0);
                  const invTot = Number(o.investigations_total || 0);
                  const total = Number(o.total_amount ?? (consult + invTot));
                  const m = o.mode_split || { CASH:0, UPI:0, PMJAY:0, PACKAGE:0, CARD:0 };
                  return (
                    <tr key={o.id} className="border-b" data-testid={`report-opd-row-${o.id}`}>
                      <td className="p-2 font-mono text-xs">{o.opd_no}</td>
                      <td className="font-mono text-xs">{o.uhid}</td>
                      <td className="text-xs">{o.patient_name}</td>
                      <td className="text-xs">{isWalkin ? <><i>Walk-in</i> · Investigations</> : <>{o.doctor_name} · {o.department}</>}</td>
                      <td className="text-xs">{fmtDT(o.created_at)}</td>
                      <td className="text-right font-mono text-xs">{consult ? fmtINR(consult) : "—"}</td>
                      <td className="text-right font-mono text-xs">{invTot ? fmtINR(invTot) : "—"}</td>
                      <td className="text-right font-mono text-xs text-teal-700">{m.CASH ? fmtINR(m.CASH) : "—"}</td>
                      <td className="text-right font-mono text-xs text-blue-700">{m.UPI ? fmtINR(m.UPI) : "—"}</td>
                      <td className="text-right font-mono text-xs text-purple-700">{m.PMJAY ? fmtINR(m.PMJAY) : "—"}</td>
                      <td className="text-right font-mono text-xs text-purple-700">{m.PACKAGE ? fmtINR(m.PACKAGE) : "—"}</td>
                      <td className="text-right font-mono text-xs">{m.CARD ? fmtINR(m.CARD) : "—"}</td>
                      <td className="text-right font-mono text-xs font-semibold">{fmtINR(total)}</td>
                    </tr>
                  );
                })}
                {(() => {
                  const tC = data.opds.reduce((s,o)=>s+Number(o.consultation_fee||0),0);
                  const tI = data.opds.reduce((s,o)=>s+Number(o.investigations_total||0),0);
                  const tot = data.opds.reduce((a,o)=>{
                    const m = o.mode_split||{};
                    return { CASH:a.CASH+Number(m.CASH||0), UPI:a.UPI+Number(m.UPI||0), PMJAY:a.PMJAY+Number(m.PMJAY||0), PACKAGE:a.PACKAGE+Number(m.PACKAGE||0), CARD:a.CARD+Number(m.CARD||0) };
                  }, { CASH:0, UPI:0, PMJAY:0, PACKAGE:0, CARD:0 });
                  return (
                    <tr className="border-t-2 border-slate-700 font-semibold bg-slate-50" data-testid="report-opd-totals">
                      <td colSpan={5} className="text-right pr-2 py-1 text-xs">Totals</td>
                      <td className="text-right font-mono text-xs">{fmtINR(tC)}</td>
                      <td className="text-right font-mono text-xs">{fmtINR(tI)}</td>
                      <td className="text-right font-mono text-xs text-teal-700">{fmtINR(tot.CASH)}</td>
                      <td className="text-right font-mono text-xs text-blue-700">{fmtINR(tot.UPI)}</td>
                      <td className="text-right font-mono text-xs text-purple-700">{fmtINR(tot.PMJAY)}</td>
                      <td className="text-right font-mono text-xs text-purple-700">{fmtINR(tot.PACKAGE)}</td>
                      <td className="text-right font-mono text-xs">{fmtINR(tot.CARD)}</td>
                      <td className="text-right font-mono text-xs">{fmtINR(tC + tI)}</td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="ipd">
          <div className="bg-white border">
            <div className="p-3 border-b flex justify-between items-center"><span className="label-tiny">IPD List ({data.ipds.length})</span><Button size="sm" variant="outline" onClick={() => downloadCSV(`ipd_${from}_to_${to}`, data.ipds.map((o) => ({ ipd_no: o.ipd_no, uhid: o.uhid, patient: o.patient_name, doctor: o.doctor_name, ward: o.ward, bed: o.bed_no, diagnosis: o.diagnosis, admission: o.admission_date, status: o.status })))}><Download className="w-3 h-3 mr-1"/> CSV</Button></div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">IPD No</th><th className="label-tiny">Patient</th><th className="label-tiny">Doctor</th><th className="label-tiny">Ward</th><th className="label-tiny">Bed</th><th className="label-tiny">Admission</th><th className="label-tiny">Status</th></tr></thead>
              <tbody>
                {data.ipds.map((o) => (
                  <tr key={o.id} className="border-b"><td className="p-2 font-mono text-xs">{o.ipd_no}</td><td>{o.patient_name}</td><td>{o.doctor_name}</td><td className="text-xs">{o.ward}</td><td className="font-mono text-xs">{o.bed_no}</td><td className="text-xs">{fmtDT(o.admission_date)}</td><td><Badge className="rounded-sm text-[10px]">{o.status}</Badge></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <PaymentTable title="Payments Received (Cash / GPay / UPI / Card etc.)" rows={data.payments} name={`payments_${from}_to_${to}`} canVoid={canVoidPayment} canRestore={canRestorePayment} canEdit={canEditPayment} onVoid={voidPayment} onRestore={restorePayment} onEdit={editPayment}/>
        </TabsContent>

        <TabsContent value="advances">
          <PaymentTable title="Advance Payments" rows={data.advances} name={`advances_${from}_to_${to}`} canVoid={canVoidPayment} canRestore={canRestorePayment} canEdit={canEditPayment} onVoid={voidPayment} onRestore={restorePayment} onEdit={editPayment}/>
        </TabsContent>

        <TabsContent value="refunds">
          <PaymentTable title="Refunds Issued" rows={data.refunds} name={`refunds_${from}_to_${to}`} canVoid={canVoidPayment} canRestore={canRestorePayment} canEdit={canEditPayment} onVoid={voidPayment} onRestore={restorePayment} onEdit={editPayment}/>
        </TabsContent>

        <TabsContent value="items">
          <div className="bg-white border">
            <div className="p-3 border-b flex justify-between items-center"><span className="label-tiny">Bill Items / Debit Charges ({data.bill_items.length})</span><Button size="sm" variant="outline" onClick={() => downloadCSV(`billitems_${from}_to_${to}`, data.bill_items)}><Download className="w-3 h-3 mr-1"/> CSV</Button></div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Date</th><th className="label-tiny">Patient</th><th className="label-tiny">Category</th><th className="label-tiny">Description</th><th className="label-tiny">Doctor</th><th className="label-tiny text-right">Qty</th><th className="label-tiny text-right">Rate</th><th className="label-tiny text-right">Amount</th>{(canVoidPayment || canRestorePayment) && <th className="label-tiny text-center w-20">Action</th>}</tr></thead>
              <tbody>
                {data.bill_items.map((i) => {
                  const voided = !!i.is_void;
                  return (
                    <tr key={i.id} className={`border-b ${voided ? "bg-red-50/40 text-slate-400" : ""}`} data-testid={`rep-bill-row-${i.id}`}>
                      <td className={`p-2 text-xs ${voided ? "line-through" : ""}`}>{fmtDT(i.date)}</td>
                      <td className={voided ? "line-through" : ""}>{i.patient_name || "-"}</td>
                      <td className={`text-xs ${voided ? "line-through" : ""}`}>{i.category}</td>
                      <td className={`text-xs ${voided ? "line-through" : ""}`}>
                        {i.description}
                        {voided && <div className="text-[10px] not-italic text-red-700 mt-0.5">VOID · {i.void_reason} · by {i.voided_by_name || "—"}</div>}
                      </td>
                      <td className={`text-xs ${voided ? "line-through" : ""}`}>{i.doctor_name || "-"}</td>
                      <td className={`text-right font-mono ${voided ? "line-through" : ""}`}>{i.quantity}</td>
                      <td className={`text-right font-mono ${voided ? "line-through" : ""}`}>{fmtINR(i.rate)}</td>
                      <td className={`text-right font-mono ${voided ? "line-through" : ""}`}>{fmtINR(i.amount)}</td>
                      {(canVoidPayment || canRestorePayment) && (
                        <td className="text-center">
                          {voided ? (
                            canRestorePayment && <button onClick={() => restoreBillItem(i.id)} className="text-[10px] text-teal-700 border border-teal-300 rounded-sm px-1.5 py-0.5 hover:bg-teal-50" data-testid={`rep-restore-bill-${i.id}`}>Restore</button>
                          ) : canVoidPayment && (
                            <button onClick={() => voidBillItem(i.id)} className="text-[10px] text-red-700 border border-red-300 rounded-sm px-1.5 py-0.5 hover:bg-red-50" data-testid={`rep-void-bill-${i.id}`}>Void</button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                <tr className="bg-slate-100 font-semibold"><td colSpan={(canVoidPayment || canRestorePayment) ? "8" : "7"} className="p-2 text-right">TOTAL</td><td className="text-right font-mono">{fmtINR(t.bill_items_total)}</td>{(canVoidPayment || canRestorePayment) && <td></td>}</tr>
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {editingPay && (
        <PaymentModeEditor
          payment={editingPay}
          onClose={() => setEditingPay(null)}
          onSaved={() => { setEditingPay(null); load(); }}
        />
      )}
    </div>
  );
}


function PaymentTable({ title, rows, name, canVoid, canRestore, canEdit, onVoid, onRestore, onEdit }) {
  const total = rows.filter((r) => !r.is_void).reduce((s, r) => s + r.amount, 0);
  const showAction = canVoid || canRestore || canEdit;
  return (
    <div className="bg-white border">
      <div className="p-3 border-b flex justify-between items-center"><span className="label-tiny">{title} ({rows.length})</span><Button size="sm" variant="outline" onClick={() => downloadCSV(name, rows)}><Download className="w-3 h-3 mr-1"/> CSV</Button></div>
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Receipt</th><th className="label-tiny">Date</th><th className="label-tiny">Patient</th><th className="label-tiny">UHID</th><th className="label-tiny">Mode</th><th className="label-tiny">Notes</th><th className="label-tiny text-right">Amount</th>{showAction && <th className="label-tiny text-center w-20">Action</th>}</tr></thead>
        <tbody>
          {rows.map((p) => {
            const voided = !!p.is_void;
            return (
              <tr key={p.id} className={`border-b ${voided ? "bg-red-50/40 text-slate-400" : ""}`} data-testid={`rep-pay-row-${p.id}`}>
                <td className={`p-2 font-mono text-xs ${voided ? "line-through" : ""}`}>{p.receipt_no}</td>
                <td className={`text-xs ${voided ? "line-through" : ""}`}>{fmtDT(p.created_at)}</td>
                <td className={voided ? "line-through" : ""}>{p.patient_name || "-"}</td>
                <td className={`font-mono text-xs ${voided ? "line-through" : ""}`}>{p.uhid || "-"}</td>
                <td className={voided ? "line-through" : ""}>{p.mode}</td>
                <td className={`text-xs text-slate-600 ${voided ? "line-through" : ""}`}>
                  {p.notes || ""}
                  {voided && <div className="text-[10px] not-italic text-red-700 mt-0.5">VOID · {p.void_reason} · by {p.voided_by_name || "—"}</div>}
                </td>
                <td className={`text-right font-mono ${voided ? "line-through" : ""}`}>{fmtINR(p.amount)}</td>
                {showAction && (
                  <td className="text-center">
                    {voided ? (
                      canRestore && <button onClick={() => onRestore(p.id)} className="text-[10px] text-teal-700 border border-teal-300 rounded-sm px-1.5 py-0.5 hover:bg-teal-50" data-testid={`rep-restore-pay-${p.id}`}>Restore</button>
                    ) : (
                      <div className="inline-flex items-center gap-1">
                        {canEdit && (
                          <button onClick={() => onEdit(p)} className="text-[10px] text-blue-700 border border-blue-300 rounded-sm px-1.5 py-0.5 hover:bg-blue-50" data-testid={`rep-edit-pay-${p.id}`}>Edit</button>
                        )}
                        {canVoid && (
                          <button onClick={() => onVoid(p.id)} className="text-[10px] text-red-700 border border-red-300 rounded-sm px-1.5 py-0.5 hover:bg-red-50" data-testid={`rep-void-pay-${p.id}`}>Void</button>
                        )}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
          <tr className="bg-slate-100 font-semibold"><td colSpan={showAction ? "6" : "6"} className="p-2 text-right">TOTAL (active)</td><td className="text-right font-mono">{fmtINR(total)}</td>{showAction && <td></td>}</tr>
        </tbody>
      </table>
    </div>
  );
}
